import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Badge } from '../ui';
import { UsuarioContext } from '../../providers/personaContext';
import { puedeAbrirFichas } from '../../providers/rolesFicha';
import { TIPOS_CUENTA } from './api';

/**
 * El buscador del listado de Personas, en su parte de CORREOS (30/10; ajuste visual 31/10):
 *
 *   · `GruposDelBuscador`  la banda «Buzones y listas que coinciden», DENTRO del mismo contenedor
 *                          que la tabla, entre el buscador y su cabecera. Una línea por grupo, con
 *                          sus miembros como chips. Los miembros llegan ya filtrados por el servidor
 *                          con las reglas del directorio (un General sólo ve a quien está de alta);
 *                          aquí sólo se decide si el chip enlaza a la ficha, con la misma regla que
 *                          el resto de la pantalla (`puedeAbrirFichas`).
 *   · `CeldaCorreo`        la celda «Mail Empresa»: el principal y, si la búsqueda coincidió con un
 *                          secundario, ese secundario resaltado debajo.
 *
 * Letra: 13px como mínimo en la banda; 12px sólo en la cabecera y en «Lo usan».
 */

const MAX_VISIBLES = 3;

const palabrasDe = (busqueda) => String(busqueda || '').trim().toLowerCase().split(/\s+/).filter(Boolean);

/**
 * El texto con cada aparición de las palabras buscadas dentro de un <mark>. Sin padding horizontal:
 * con él, «sop|orte» se veía partido en dos por un hueco en medio de la palabra.
 */
export function Resaltado({ texto, busqueda }) {
  const palabras = palabrasDe(busqueda);
  const t = String(texto || '');
  if (!palabras.length || !t) return t;
  const escapar = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const partes = t.split(new RegExp(`(${palabras.map(escapar).join('|')})`, 'ig'));
  return partes.map((p, i) => (palabras.includes(p.toLowerCase())
    ? <mark key={i} className="rounded-[2px] bg-amber-100 p-0 text-inherit">{p}</mark>
    : <React.Fragment key={i}>{p}</React.Fragment>));
}

export function CeldaCorreo({ fila, busqueda }) {
  const coincidentes = Array.isArray(fila.correos_coincidentes) ? fila.correos_coincidentes : [];
  const secundariosQueCoinciden = coincidentes.filter((c) => c !== fila.mail_empresa);
  const principalCoincide = coincidentes.includes(fila.mail_empresa);
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span>{principalCoincide ? <Resaltado texto={fila.mail_empresa} busqueda={busqueda} /> : fila.mail_empresa}</span>
      {secundariosQueCoinciden.map((c) => (
        <span key={c} className="text-xs text-slate-600" title="Ha coincidido por este correo secundario">
          <Resaltado texto={c} busqueda={busqueda} /> <span className="text-[10px] uppercase tracking-wide text-slate-400">secundario</span>
        </span>
      ))}
    </span>
  );
}

const rutaFicha = (m) => (m.ambito === 'colaborador'
  ? `/gestionpersonas/personas-colaboradoras/editarColaborador/${m.id}`
  : `/gestionpersonas/personas/editarPersona/${m.id}`);

/** Un miembro: avatar de 26px y nombre completo. Enlace a la ficha sólo si el rol puede abrirlas. */
function ChipMiembro({ m, conEnlace }) {
  const nombre = `${m.nombre || ''} ${m.apellidos || ''}`.trim() || `#${m.id}`;
  const clases = 'inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-0.5 pr-2.5 text-[13px] text-slate-800';
  const contenido = (
    <>
      <Avatar name={nombre} className="h-[26px] w-[26px]" title={m.mail_empresa || nombre} />
      {nombre}
    </>
  );
  return conEnlace
    ? <Link to={rutaFicha(m)} title={m.mail_empresa || undefined} className={`${clases} hover:border-slate-300 hover:bg-slate-100`}>{contenido}</Link>
    : <span title={m.mail_empresa || undefined} className={clases}>{contenido}</span>;
}

function LineaGrupo({ g, busqueda, conEnlace }) {
  const t = TIPOS_CUENTA[g.tipo] || TIPOS_CUENTA.buzon;
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <Badge variant={t.badge} className="text-[13px]">{t.corto}</Badge>
      <span className="break-all text-[14px] font-semibold text-slate-900"><Resaltado texto={g.correo} busqueda={busqueda} /></span>
      <span className="text-[13px] text-slate-500"><Resaltado texto={g.nombre} busqueda={busqueda} /></span>
      <span aria-hidden="true" className="h-5 w-px bg-slate-300" />
      <span className="text-xs text-slate-500">Lo usan</span>
      {g.miembros.length === 0
        ? <span className="text-[13px] text-slate-400">nadie visible</span>
        : g.miembros.map((m) => <ChipMiembro key={`${m.ambito}-${m.id}`} m={m} conEnlace={conEnlace} />)}
    </li>
  );
}

/**
 * La banda. `Ocultar` y `Ver todos` son estado local: no persisten. El padre la monta con
 * `key={búsqueda}`, así que cada búsqueda nueva empieza desplegada y con los tres primeros.
 */
export default function GruposDelBuscador({ grupos, busqueda }) {
  const { persona } = useContext(UsuarioContext) || {};
  const conEnlace = puedeAbrirFichas(persona?.rol);
  const [oculto, setOculto] = useState(false);
  const [todos, setTodos] = useState(false);
  if (!grupos || !grupos.length) return null;
  const visibles = todos ? grupos : grupos.slice(0, MAX_VISIBLES);

  return (
    <section aria-label="Buzones y listas que coinciden" className="border-b border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Buzones y listas que coinciden · {grupos.length}
        </h3>
        <button type="button" onClick={() => setOculto((o) => !o)} aria-expanded={!oculto}
          className="rounded px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-200 hover:text-slate-900">
          {oculto ? 'Mostrar' : 'Ocultar'}
        </button>
      </div>
      {!oculto && (
        <>
          <ul className="m-0 mt-2 list-none space-y-2 p-0">
            {visibles.map((g) => <LineaGrupo key={g.id} g={g} busqueda={busqueda} conEnlace={conEnlace} />)}
          </ul>
          {grupos.length > MAX_VISIBLES && !todos && (
            <button type="button" onClick={() => setTodos(true)}
              className="mt-2 text-[13px] font-medium text-[hsl(var(--brand-light))] hover:underline">
              Ver todos ({grupos.length})
            </button>
          )}
        </>
      )}
    </section>
  );
}
